if (!customElements.get('ftd-signup-popup')) {
  customElements.define('ftd-signup-popup', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      if (!this.dialog || !this.form || !this.dialog.showModal) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.designMode = Boolean(window.Shopify?.designMode);
      this.storageKey = 'ftd-signup-v1';
      this.pendingKey = `${this.storageKey}-pending`;
      this.querySelector('[data-signup-close]').addEventListener('click', () => this.close(), options);
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      }, options);
      this.dialog.addEventListener('click', (event) => {
        const rect = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) this.close();
      }, options);
      this.dialog.addEventListener('close', () => document.body.classList.remove('ftd-signup-open'), options);
      this.querySelector('[data-signup-continue]').addEventListener('click', () => this.close(), options);
      this.form.addEventListener('submit', (event) => {
        if (this.designMode) {
          event.preventDefault();
          this.querySelector('[data-signup-preview-message]').hidden = false;
          return;
        }
        this.write('sessionStorage', this.pendingKey, { time: Date.now(), form: this.form.id });
        // Let Shopify finish any submission it has already intercepted for CAPTCHA.
        if (event.defaultPrevented) return;
        // Without a ready CAPTCHA token, keep Shopify's native verification flow.
        if (window.Shopify?.captcha && !new FormData(this.form).get('h-captcha-response')) return;
        event.preventDefault();
        this.submitSignup();
      }, options);
      document.addEventListener('shopify:section:select', (event) => {
        if (this.designMode && event.detail.sectionId === this.dataset.sectionId) this.open();
      }, options);
      document.addEventListener('shopify:section:deselect', (event) => {
        if (this.designMode && event.detail.sectionId === this.dataset.sectionId) this.close();
      }, options);

      if (this.designMode) {
        if (this.dataset.preview === 'true') this.open();
        return;
      }
      const state = this.querySelector('[data-signup-state]').dataset.signupState;
      const pending = this.read('sessionStorage', this.pendingKey);
      const isOurReturn = (pending?.form === this.form.id && Date.now() - pending.time < 600000) || location.hash === `#${this.form.id}`;
      if (state !== 'idle' && isOurReturn) {
        this.write('sessionStorage', this.pendingKey, null);
        if (state === 'success') this.showSuccess();
        this.open();
        return;
      }
      // A successful footer/newsletter signup should not trigger another signup prompt.
      if (state === 'success') {
        this.write('localStorage', this.storageKey, { subscribed: true });
        return;
      }
      const saved = this.read('localStorage', this.storageKey);
      const shownRecently = saved?.lastShown && Date.now() - saved.lastShown < 86400000;
      if (this.dataset.enabled !== 'true' || this.dataset.subscribed === 'true' || saved?.subscribed || shownRecently) return;
      this.timer = setTimeout(() => this.openWhenAvailable(), Number(this.dataset.delay) * 1000);
    }

    showSuccess() {
      this.write('sessionStorage', this.pendingKey, null);
      this.write('localStorage', this.storageKey, { subscribed: true });
      this.querySelector('[data-signup-pitch]').hidden = true;
      this.querySelector('[data-signup-consent]').hidden = true;
      this.querySelector('[data-signup-error]').hidden = true;
      this.form.hidden = true;
      const success = this.querySelector('[data-signup-success]');
      success.hidden = false;
      this.dialog.setAttribute('aria-labelledby', success.querySelector('h2').id);
      this.dialog.setAttribute('aria-describedby', success.querySelector('p').id);
      if (this.dialog.open) success.querySelector('h2').focus({ preventScroll: true });
    }

    async submitSignup() {
      if (this.submitting) return;
      this.submitting = true;
      const button = this.form.querySelector('[type="submit"]');
      const label = button.textContent;
      const error = this.querySelector('[data-signup-error]');
      error.hidden = true;
      button.disabled = true;
      button.textContent = 'Signing you up…';
      this.form.setAttribute('aria-busy', 'true');
      const request = new AbortController();
      this.request = request;
      const timeout = setTimeout(() => request.abort(), 20000);
      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: new FormData(this.form),
          credentials: 'same-origin',
          signal: request.signal
        });
        const url = new URL(response.url);
        if (url.origin === location.origin && /\/challenge(?:\/|$)/.test(url.pathname)) {
          location.assign(url.href);
          return;
        }
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const returnedForm = html.getElementById(this.form.id);
        const state = returnedForm?.querySelector('[data-signup-state]')?.dataset.signupState;
        if (response.ok && (state === 'success' || url.searchParams.get('customer_posted') === 'true')) {
          this.showSuccess();
        } else {
          const message = returnedForm?.querySelector('[role="alert"]')?.textContent.trim();
          throw new Error(message || 'We couldn’t confirm your signup. Please try again.');
        }
      } catch (failure) {
        if (!this.isConnected) return;
        error.textContent = failure.name === 'AbortError' || failure instanceof TypeError
          ? 'We couldn’t confirm your signup. Check your connection and try again.'
          : failure.message;
        error.hidden = false;
        this.write('sessionStorage', this.pendingKey, null);
      } finally {
        clearTimeout(timeout);
        this.submitting = false;
        button.disabled = false;
        button.textContent = label;
        this.form.removeAttribute('aria-busy');
      }
    }

    read(storage, key) {
      try { return JSON.parse(window[storage].getItem(key)); } catch { return null; }
    }

    write(storage, key, value) {
      try { window[storage].setItem(key, JSON.stringify(value)); } catch { /* Storage is optional. */ }
    }

    openWhenAvailable() {
      const otherModalOpen = Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"], cart-drawer.active, details[open] .modal__content'))
        .some((element) => element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden');
      if (document.hidden || otherModalOpen) {
        this.timer = setTimeout(() => this.openWhenAvailable(), 2000);
        return;
      }
      this.open();
    }

    open() {
      if (!this.isConnected || this.dialog.open) return;
      this.dialog.showModal();
      if (!this.designMode) {
        const saved = this.read('localStorage', this.storageKey);
        // Count the cooldown from display, even if the visitor navigates away without closing.
        if (!saved?.subscribed) this.write('localStorage', this.storageKey, { lastShown: Date.now() });
      }
      document.body.classList.add('ftd-signup-open');
      (this.querySelector('[data-signup-success]:not([hidden]) h2') || this.querySelector('[data-signup-close]')).focus({ preventScroll: true });
    }

    close() {
      this.dialog.close();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.request?.abort();
      if (this.dialog?.open) {
        this.dialog.close();
        document.body.classList.remove('ftd-signup-open');
      }
    }
  });
}
